import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { DefaultComponentsProvider } from "~/components/content/content-item-render";
import type { searchContentItemContent$key } from "~/queries/__generated__/searchContentItemContent.graphql";

import {
  Container,
  Content,
  Cover,
  Footer,
  Header,
  LiveTagContainer,
  PrintHeadline,
  StyledEntityLink,
} from "./styles";

export type Props = {
  className?: string;
  provider: DefaultComponentsProvider;
  reference: searchContentItemContent$key;
};

export const ContentItemSearch = forwardRef<HTMLDivElement, Props>(
  ({ className, provider, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment searchContentItemContent on Content {
          ...entityLink
        }
      `,
      reference_,
    );

    const header = (
      <>
        <LiveTagContainer>{provider.liveTag()}</LiveTagContainer>
        {provider.headline({
          hideTooltip: true,
          useSectionTags: true,
        })}
      </>
    );

    const cover = provider.coverImage({
      responsiveVariants: { desktopUp: "size80x80", mobileUp: "size80x80", tabletUp: "size80x80" },
    });
    const context = provider.sponsorTag ? null : provider.summary();
    const footer =
      provider.sponsorTag?.({
        color: "#001246",
        gap: "4px",
        layoutDirection: "horizontal",
        nameFontSize: "12px",
        typeFontSize: "12px",
      }) ?? provider.actionBar({ isBookmarkVisible: false, isCommentsVisible: false });

    return (
      <Container className={className} ref={reference}>
        <StyledEntityLink reference={content}>
          <Header>{header}</Header>
          <Cover>{cover}</Cover>
          <Content>{context}</Content>
          <Footer>{footer}</Footer>
          <PrintHeadline>{provider.printHeadline()}</PrintHeadline>
        </StyledEntityLink>
      </Container>
    );
  },
);

ContentItemSearch.displayName = "ContentItemSearch";
